import { createNameTag } from '../rendering/utils/NameTag';
import { bootSearchParams } from '../../config/boot/bootUrl';

/**
 * Debug spell-cast text bubbles — floating ability names above characters
 * when abilities fire. The single most important debug tool for visual QA:
 * without it, DJ can't tell if an ability fired, what it was, or whether
 * the server acknowledged it.
 *
 * Toggle: ?debugSpellText=1 (or ?debug=1 which enables all debug overlays)
 * NOT active in production builds — gated behind URL param.
 *
 * Phase H — substrate-aware via createNameTag + rAF (float-up + alpha
 * fade) so text renders on the Pixi overlay under ?renderer=pixi too.
 */

function isEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const params = bootSearchParams();
    return params.get('debugSpellText') === '1' || params.get('debug') === '1';
  } catch {
    return false;
  }
}

const DURATION_MS = 2000;
const FLOAT_DISTANCE = 40;
const DEPTH = 100; // above entities, below debug overlays

/**
 * Show ability-name floating text at the given world position.
 * Safe to call at any time — silently no-ops when the feature is disabled.
 */
export function showSpellText(
  scene: unknown,
  abilityName: string,
  x: number,
  y: number,
): void {
  if (!isEnabled()) return;
  if (!scene || !abilityName) return;

  const tag = createNameTag(scene, x, y, abilityName, {
    depth: DEPTH,
    fontSize: 14,
    color: 0xffdd44,
    fontWeight: 'bold',
    strokeWidth: 3,
    anchor: { x: 0.5, y: 0.5 },
  });

  // rAF: float upward by FLOAT_DISTANCE while fading alpha to 0 over
  // DURATION_MS, then destroy. Power2 ease in Phaser ≈ t² — we apply
  // ease(t) = t * t to the y interp and 1 - t * t to alpha for parity.
  const startTime = performance.now();
  const startY = y;
  const tick = () => {
    const elapsed = performance.now() - startTime;
    const t = Math.min(elapsed / DURATION_MS, 1);
    const ease = t * t; // Power2
    tag.setPosition(x, startY - FLOAT_DISTANCE * ease);
    tag.setAlpha(1 - ease);
    if (t < 1) requestAnimationFrame(tick);
    else tag.destroy();
  };
  requestAnimationFrame(tick);
}

/**
 * Show spell text above the local player sprite.
 * Call after a successful ability execution.
 */
export function showSpellTextForLocal(
  abilityName: string,
  playerSprite: { x: number; y: number; scene: unknown } | null | undefined,
): void {
  if (!isEnabled() || !playerSprite?.scene || !abilityName) return;
  showSpellText(playerSprite.scene, abilityName, playerSprite.x, playerSprite.y - 50);
}
